import React, { useState, useEffect, useCallback } from 'react';
import { format, addDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const statusBadge = v => {
  const map = { booked:'bg-blue-100 text-blue-700', completed:'bg-green-100 text-green-700', cancelled:'bg-red-100 text-red-700', pending:'bg-amber-100 text-amber-700' };
  return <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize', map[v] || 'bg-slate-100 text-slate-600')}>{v || '–'}</span>;
};
const currency = v => v != null ? `$${parseFloat(v).toFixed(2)}` : '–';
const km = v => v != null ? `${parseFloat(v).toFixed(1)} km` : '–';

const COLS = [
  { key: 'shift_date',    label: 'Shift Date' },
  { key: 'staff_name',    label: 'Care Worker' },
  { key: 'staff_phone',   label: 'Phone' },
  { key: 'client_names',  label: 'Client(s)' },
  { key: 'shift_type',    label: 'Shift Type' },
  { key: 'start_time',    label: 'Start Time' },
  { key: 'end_time',      label: 'End Time' },
  { key: 'hours',         label: 'Hours',        render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'break_minutes', label: 'Break (min)',   render: v => v || '0' },
  { key: 'km',            label: 'KM',            render: km },
  { key: 'km_expense',    label: 'KM Expense',    render: currency },
  { key: 'other_expense', label: 'Other Exp.',    render: currency },
  { key: 'status',        label: 'Status',        render: statusBadge },
];

export default function CareWorkerScheduleKmExp() {
  const [data, setData]         = useState([]);
  const [staff, setStaff]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [staffId, setStaffId]   = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId) p.set('staff_id', staffId);
    api.get(`/reports/care-worker-schedule-km-exp?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, staffId]);

  useEffect(() => { load(); }, [load]);

  const totals = data.reduce((acc, r) => ({
    km: acc.km + parseFloat(r.km || 0),
    km_expense: acc.km_expense + parseFloat(r.km_expense || 0),
    other_expense: acc.other_expense + parseFloat(r.other_expense || 0),
    hours: acc.hours + parseFloat(r.hours || 0),
  }), { km: 0, km_expense: 0, other_expense: 0, hours: 0 });

  const selStaff = staff.find(s => s.id === staffId);
  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selStaff ? [{ label: 'Staff', value: `${selStaff.first_name} ${selStaff.last_name}`, onRemove: () => setStaffId('') }] : []),
  ];

  return (
    <ReportLayout title="Care Worker Service Schedule With KM, EXP" data={data} filename="care_worker_schedule_km_exp" columns={COLS}
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Care Worker</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      {data.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-4">
          {[
            { label: 'Total Hours',   value: `${totals.hours.toFixed(2)}h` },
            { label: 'Total KM',      value: km(totals.km) },
            { label: 'KM Expenses',   value: currency(totals.km_expense) },
            { label: 'Other Expenses',value: currency(totals.other_expense) },
          ].map(card => (
            <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">{card.label}</div>
              <div className="text-lg font-bold text-slate-900">{card.value}</div>
            </div>
          ))}
        </div>
      )}
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination} />
    </ReportLayout>
  );
}
